import { useEffect, useRef, useState } from "react";
import {
  AccessibilityInfo,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type TextInput,
} from "react-native";
import { Trans, useTranslation } from "react-i18next";
import {
  authErrorCode,
  authErrorText,
  forgotPasswordErrorKey,
  unmetPasswordRules,
} from "@heraion/shared";
import { Button } from "../primitives/Button";
import { FieldLabel } from "../primitives/FieldLabel";
import { TextField } from "../primitives/TextField";
import { useCountTimer } from "../useCountTimer";
import { useTimerDisplay } from "../useTimerDisplay";
import { CodeField } from "./CodeField";
import { NewPasswordFields, passwordMatch } from "./NewPasswordFields";
import { authFormStyles } from "./authFormStyles";
import type { UiTheme } from "../theme";
import { stateAria } from "../a11y";

// RW-49 — la porta di RECUPERO, montata su mobile e web. Fratello di
// <WelcomeForm>: stessa divisione del lavoro (il form sa il flusso, il mount
// sa solo cablare la piattaforma) e stesse convenzioni di colonna.
//
// ⭐ Perché nasce condiviso invece che copiato sul web. Era l'ULTIMA porta
// d'accesso rimasta solo-mobile, e il web non ne aveva nessuna: chi usa solo
// il browser non poteva rientrare, perché la vecchia web che una porta ce
// l'aveva è spenta dal 21/08.
//
// ⚡ PROF-5 (Antenore, 3/09, dopo il suo reset riuscito) — TRE passi, non due.
// Erano due, e il codice stava sullo stesso schermo della password nuova. Le
// tre cose che ha chiesto, e dove vivono:
//   1. il codice in sei caselle, che accetta l'incolla → `CodeField`
//   2. i campi del tipo giusto, perché i password manager riempivano il codice
//      con lo username → sempre `CodeField`, che si dichiara `one-time-code`
//   3. la conferma della password → `NewPasswordFields`, ESTRATTO da
//      `WelcomeForm` invece che copiato
//
// ⚠️⚠️ **Cognito NON sa verificare un codice da solo.** `ConfirmForgotPassword`
// vuole codice E password nuova nella stessa chiamata: fra il secondo schermo
// e il terzo non esiste un passo di rete. Il passo 2 controlla quindi la sola
// FORMA (sei cifre) e prosegue; un codice sbagliato si scopre all'invio finale.
// ⇒ Per questo il link in fondo, dal passo 2 in poi, torna al passo PRECEDENTE.
//
// ⚡ PROF-6 + PROF-7 (Antenore, 4/09) — il passo indietro da SOLO non bastava.
// Sue parole: *«di solito i siti non danno un certo numero di tentativi? Non
// dovrebbe anche essere possibile chiedere un nuovo codice o tornare alla
// login?»*. Aveva ragione, e la misura gli ha dato ragione tre volte: il rinvio
// esisteva solo per caso (indietro fino all'email, ripremi «Invia codice»),
// l'uscita all'accesso costava tre tap dal passo 3, e i tre passi portavano lo
// stesso titolo. Adesso lo schermo del codice ha le TRE affordance che ha
// chiesto, e ogni passo dice qual è.
// ⚠️ I tentativi li conta COGNITO, non noi: `TooManyFailedAttemptsException` e
// `LimitExceededException` arrivano da lì. ⛔ Nessun contatore in questo file —
// un secondo conteggio sarebbe una bugia sul limite vero.
//
// ⚠️ `preventUserExistenceErrors` è attivo sul pool, quindi anche un'email
// SCONOSCIUTA passa al secondo passo — la superficie resta opaca di proposito
// e questo form non deve tradire se quell'indirizzo esiste. È esattamente il
// meccanismo che il 3/09 ha fatto sembrare rotto un flusso che funzionava
// (`OP-23`): l'indirizzo provato non esisteva nel pool.
type Step = "email" | "code" | "password";

const CODE_LENGTH = 6;
// ⛔ Un'attesa NOSTRA, non quella di Cognito, che il suo limite non lo dichiara.
// Serve a non far martellare il tasto: se scade prima che la mail arrivi, il
// rinvio è di nuovo lì. Un valore più lungo punirebbe chi la mail non l'ha
// ricevuta davvero.
const RESEND_COOLDOWN_SEC = 60;
const STEPS: readonly Step[] = ["email", "code", "password"];

export type ForgotPasswordFormProps = {
  theme: UiTheme;
  /** Chiede a Cognito di spedire il codice.
   *  ⛔ Rigetta col rigetto GREZZO di Cognito (`{ code, message }`), non con un
   *  `new Error(message)`: è il `code` che sceglie la traduzione, e i due mount
   *  lo buttavano via lasciando all'atleta la frase inglese. */
  onRequestCode: (email: string) => Promise<void>;
  /** Chiude il reset con codice + password nuova. Rigetta come sopra. */
  onConfirm: (params: {
    email: string;
    code: string;
    newPassword: string;
  }) => Promise<void>;
  /** Reset riuscito: il mount decide dove tornare, e con quale messaggio. */
  onDone: (message: string) => void;
  /** Torna alla porta d'accesso senza aver cambiato niente. */
  onBack: () => void;
};

export function ForgotPasswordForm({
  theme,
  onRequestCode,
  onConfirm,
  onDone,
  onBack,
}: ForgotPasswordFormProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  // DS-67 — UN interruttore per i due campi password. ⛔ Lo stato sta QUI e non
  // dentro `NewPasswordFields`: solo il form sa quando si è premuto «invia», e
  // la garanzia è che a ogni tentativo le password tornino coperte.
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ⛔ Separata dall'errore, non lo stesso slot con un colore diverso: «codice
  // rinviato» è una conferma, e riusare la riga rossa direbbe che è andata male.
  const [notice, setNotice] = useState<string | null>(null);
  // ⛔ Il motore del countdown è quello del logger (`useCountTimer`), non un
  // `setInterval` a mano: è React puro, gira identico sul web, e i suoi due
  // hook di notifica sono opzionali — un cooldown non ne vuole nessuno.
  const cooldown = useCountTimer();
  // I secondi visibili vengono dalla lettura dello schermo (#326): il motore
  // si sveglia solo sul 3-2-1 e sullo zero, e il suo `remainingSec` resta
  // fermo in mezzo.
  const coolingSec = useTimerDisplay(cooldown.state).displaySec;
  const cooling = coolingSec > 0;
  // ⛔ Il motore tiene `endsAt` anche DOPO lo zero: senza questo, un cooldown
  // finito resterebbe un timer «in corso» fermo a zero per tutto il tempo che
  // il form resta a schermo.
  const { atZero } = cooldown.state;
  const resetCooldown = cooldown.reset;
  useEffect(() => {
    if (atZero) resetCooldown();
  }, [atZero, resetCooldown]);
  // Quale campo è in errore, non «il form è in errore» — marcare tutto per una
  // password corta direbbe che anche il codice è sbagliato.
  const [badField, setBadField] = useState<
    "email" | "code" | "password" | "confirm" | null
  >(null);
  const codeRef = useRef<TextInput>(null);

  // ⛔ UN annuncio per cambiamento, non due. `announceForAccessibility` parla
  // su iOS E su Android, ed è inerte su react-native-web; `accessibilityLiveRegion`
  // esiste su Android e sul web. Metterli entrambi senza condizioni fa dire la
  // stessa frase DUE volte a TalkBack — l'anti-pattern che `useTransientTip.tsx:69`
  // ha già misurato e scritto. Il ramo esplicito copre quindi il solo iOS, e la
  // live-region copre Android e web.
  const announce = (message: string) => {
    if (Platform.OS === "ios") AccessibilityInfo.announceForAccessibility(message);
  };

  const fail = (field: typeof badField, message: string) => {
    setBadField(field);
    setNotice(null);
    setError(message);
    announce(message);
  };

  // ⛔ Un solo posto in cui un rigetto di Cognito diventa una frase. La tabella
  // è in `@heraion/shared` accanto a quella del login, che NON si poteva
  // allargare: quella collassa due codici in una stringa opaca per non far
  // enumerare gli indirizzi, questi quattro vogliono l'opposto.
  // ⚠️ Il fallback resta il testo di Cognito, in inglese: è un codice che non
  // abbiamo mappato, e mostrarlo è meglio che inghiottirlo.
  const failFrom = (err: unknown, fallback: string) => {
    const key = forgotPasswordErrorKey(authErrorCode(err));
    fail(null, key !== null ? t(key) : authErrorText(err, fallback));
  };

  // ⛔ Il corpo dello schermo si scambia sotto il cursore di chi non vede:
  // senza l'annuncio resta fermo su un campo che non esiste più, nel momento
  // di massima ansia di tutto il flusso.
  const goTo = (next: Step, spoken?: string) => {
    setStep(next);
    setError(null);
    setNotice(null);
    setBadField(null);
    if (spoken !== undefined) announce(spoken);
  };

  const onSendCode = async () => {
    if (busy) return;
    setError(null);
    setNotice(null);
    setBadField(null);
    if (email.trim().length === 0) {
      return fail("email", t("auth.forgotEmailRequired"));
    }
    setBusy(true);
    try {
      await onRequestCode(email.trim());
      setBusy(false);
      cooldown.start({ seconds: RESEND_COOLDOWN_SEC });
      goTo("code", t("auth.forgotCodeSent"));
      // ⛔ Istantaneo, al montaggio della fase: la tastiera deve essere già
      // aperta sulle caselle, che sono il gesto successivo e l'unico possibile.
      codeRef.current?.focus();
    } catch (err) {
      setBusy(false);
      failFrom(err, "Request failed");
    }
  };

  // Lo stesso invio, senza il cambio di passo: si resta sulle caselle.
  // ⛔ Il codice digitato NON si cancella — quello vecchio potrebbe essere
  // giusto e la mail nuova arrivare dopo, e svuotare il campo butterebbe via
  // l'unica cosa che l'atleta ha in mano.
  const onResend = async () => {
    if (busy || cooling) return;
    setError(null);
    setNotice(null);
    setBadField(null);
    setBusy(true);
    try {
      await onRequestCode(email.trim());
      setBusy(false);
      cooldown.start({ seconds: RESEND_COOLDOWN_SEC });
      const sent = t("auth.resentInfo");
      setNotice(sent);
      announce(sent);
    } catch (err) {
      setBusy(false);
      failFrom(err, "Request failed");
    }
  };

  // ⚠️ Nessuna chiamata di rete: Cognito non verifica un codice da solo (vedi
  // il blocco in testa). Qui si controlla la forma e si prosegue.
  const onCodeEntered = () => {
    if (busy) return;
    setNotice(null);
    if (code.length < CODE_LENGTH) {
      return fail("code", t("auth.forgotCodeIncomplete"));
    }
    goTo("password");
  };

  const onReset = async () => {
    if (busy) return;
    setError(null);
    setNotice(null);
    setBadField(null);
    // ⛔ PRIMA delle validazioni, non solo prima della rete: chi ha scoperto le
    // password per confrontarle non deve restare con quelle a schermo perché
    // il tentativo si è fermato su una regola. Il form resta a video in
    // entrambi i casi, e in entrambi la password sarebbe leggibile a chi passa.
    setRevealed(false);
    // La policy è la configurazione del POOL, non una scelta di questo
    // schermo. ⭐ Mobile non la controllava affatto e lasciava rispondere
    // Cognito, che risponde `InvalidPasswordException` in inglese: il giro di
    // rete si evita e l'atleta legge la regola nella sua lingua.
    if (unmetPasswordRules(newPassword).length > 0) {
      return fail("password", t("auth.passwordHint"));
    }
    if (passwordMatch(newPassword, confirm) !== "match") {
      return fail("confirm", t("auth.passwordMismatch"));
    }
    setBusy(true);
    try {
      await onConfirm({ email: email.trim(), code, newPassword });
      onDone(t("auth.forgotResetDone"));
    } catch (err) {
      setBusy(false);
      failFrom(err, "Reset failed");
    }
  };

  const submit =
    step === "email" ? onSendCode : step === "code" ? onCodeEntered : onReset;
  const submitLabel =
    step === "email"
      ? t("auth.forgotSendCode")
      : step === "code"
        ? t("auth.forgotCodeSubmit")
        : t("auth.forgotResetSubmit");

  return (
    <View style={styles.wrap}>
      <View style={styles.head}>
        <Text style={styles.title}>{t("auth.forgotTitle")}</Text>
        {/* PROF-7 — i tre passi portavano lo stesso titolo, quindi niente
            diceva a che punto fossi. ⛔ Testo, non una barra di avanzamento:
            uno screen reader deve poterlo LEGGERE, e tre pallini colorati
            direbbero la stessa cosa solo a chi ci vede. */}
        <Text style={styles.stepLine}>
          {t("auth.forgotStep", { n: STEPS.indexOf(step) + 1 })}
        </Text>
      </View>

      {step === "email" ? (
        <>
          <Text style={styles.help}>{t("auth.forgotIntro")}</Text>
          <View>
            <FieldLabel theme={theme} style={styles.label}>
              {t("auth.email")}
            </FieldLabel>
            <TextField
              theme={theme}
              accessibilityLabel={t("auth.email")}
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect={false}
              editable={!busy}
              inputMode="email"
              invalid={badField === "email"}
              keyboardType="email-address"
              placeholder={t("auth.emailPlaceholder")}
              style={styles.field}
              value={email}
              onChangeText={setEmail}
            />
          </View>
        </>
      ) : null}

      {step === "code" ? (
        <>
          <Text style={styles.help} accessibilityLiveRegion="polite">
            <Trans
              i18nKey="auth.forgotConfirmHelp"
              values={{ email: email.trim() }}
              components={{ bold: <Text style={styles.bold} /> }}
            />
          </Text>
          <View>
            <FieldLabel theme={theme} style={styles.label}>
              {t("auth.confirmCode")}
            </FieldLabel>
            <CodeField
              ref={codeRef}
              theme={theme}
              accessibilityLabel={t("auth.confirmCode")}
              editable={!busy}
              invalid={badField === "code"}
              length={CODE_LENGTH}
              value={code}
              onChangeText={setCode}
            />
          </View>
        </>
      ) : null}

      {step === "password" ? (
        <>
          <Text style={styles.help}>{t("auth.forgotPasswordHelp")}</Text>
          <NewPasswordFields
            theme={theme}
            badField={
              badField === "password" || badField === "confirm" ? badField : null
            }
            confirm={confirm}
            editable={!busy}
            password={newPassword}
            revealed={revealed}
            onChangeConfirm={setConfirm}
            onChangePassword={setNewPassword}
            onToggleReveal={() => setRevealed((v) => !v)}
          />
        </>
      ) : null}

      {notice !== null ? (
        <Text style={styles.notice} accessibilityLiveRegion="polite">
          {notice}
        </Text>
      ) : null}

      {error !== null ? (
        <Text style={styles.error} accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}

      <Button theme={theme} label={submitLabel} loading={busy} onPress={submit} />

      {/* ⛔ LINK, non bottoni. Il vecchio schermo mobile usciva con un link e la
          porta d'accesso ci ENTRA con un link: renderli `Button` ghost metterebbe
          tre rettangoli di peso quasi uguale sotto quello che conta. Lo stile è
          quello condiviso (`authFormStyles.linkHit`/`link`), e il pavimento dei
          44 sta lì dentro perché react-native-web ignora `hitSlop`. */}

      {/* ⛔ I tre link stanno in un GRUPPO, non sciolti nella colonna. Sciolti
          ereditavano il `gap` della porta — pensato per separare campo da campo —
          e occupavano 120dp leggendosi come tre sezioni invece che come le
          azioni secondarie di quella sopra. Visto al banco visivo, non dedotto. */}
      <View style={styles.links}>
      {/* PROF-6 — il rinvio. ⛔ Esiste solo dove serve: al passo 1 il bottone
          primario È l'invio, e al passo 3 il codice è già stato speso. */}
      {step === "code" ? (
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ disabled: busy || cooling }}
          {...stateAria({ disabled: busy || cooling })}
          disabled={busy || cooling}
          style={styles.linkHit}
          onPress={onResend}
        >
          <Text style={[styles.link, (busy || cooling) && styles.linkMuted]}>
            {cooling
              ? t("auth.resendCodeIn", { seconds: coolingSec })
              : t("auth.resendCode")}
          </Text>
        </Pressable>
      ) : null}

      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: busy }}
        {...stateAria({ disabled: busy })}
        disabled={busy}
        style={styles.linkHit}
        onPress={
          step === "email"
            ? onBack
            : () => goTo(step === "code" ? "email" : "code")
        }
      >
        <Text style={[styles.link, busy && styles.linkMuted]}>
          {step === "email" ? t("auth.backToSignIn") : t("auth.backStep")}
        </Text>
      </Pressable>

      {/* PROF-6 — l'uscita esplicita. Dal passo 3 costava tre tap, cioè
          scalare all'indietro tutta la procedura. ⛔ Al passo 1 non si ripete:
          lì il link qui sopra dice già questo. */}
      {step === "email" ? null : (
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ disabled: busy }}
          {...stateAria({ disabled: busy })}
          disabled={busy}
          style={[styles.linkHit, styles.linkExit]}
          onPress={onBack}
        >
          <Text style={[styles.link, busy && styles.linkMuted]}>
            {t("auth.backToSignIn")}
          </Text>
        </Pressable>
      )}
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return {
    // La colonna la porta il modulo condiviso (authFormStyles); qui restano
    // solo le due cose che sono di questo form.
    ...authFormStyles(theme),
    ...StyleSheet.create({
      bold: { fontWeight: "700", color: p.ink },
      // Titolo e passo sono una cosa sola: `wrap` ha un `gap`, e senza questo
      // il numero del passo galleggerebbe lontano dal titolo che qualifica.
      head: { gap: s.s1 },
      // ⛔ `s1`, non il gap della colonna: i link sono un gruppo solo.
      links: { gap: s.s1 },
      // ⛔ Le prime due azioni si RECUPERANO (rispedisci, torna di un passo);
      // questa ABBANDONA la procedura. Con lo stesso stacco delle altre si
      // leggevano come tre cose uguali, e la terza non lo è.
      linkExit: { marginTop: s.s2 },
      // Il numero del passo è informazione da LEGGERE, non un'etichetta di
      // controllo: sta a `base`, non alla taglia dei link.
      stepLine: { color: p.ink2, fontSize: ty.sizes.base },
      notice: { color: p.ink2, fontSize: ty.sizes.base },
      // ⛔ Non è «solo colore»: il link spento è anche `disabled`, quindi
      // TalkBack e lo screen reader web lo annunciano come tale, e il testo
      // stesso cambia — dice i secondi che mancano.
      linkMuted: { color: p.ink2 },
    }),
  };
}
